import React, { useState } from 'react';
import { DynamicIcon } from './DynamicIcon';
import { insuranceTypes } from '../types/insurance';

interface InsuranceFormProps {
  selectedInsurances: string[];
  onBack: () => void;
  onComplete: () => void;
}

export function InsuranceForm({ selectedInsurances, onBack, onComplete }: InsuranceFormProps) {
  const [formData, setFormData] = useState<{ [key: string]: any }>({});

  const handleInputChange = (insuranceId: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [insuranceId]: {
        ...prev[insuranceId],
        [field]: value
      }
    }));
  };

  const renderFormFields = (fields: any[], insuranceId: string) => {
    return fields.map(field => {
      switch (field.type) {
        case 'text':
          return (
            <div key={field.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <input
                type="text"
                required={field.required}
                placeholder={field.placeholder}
                className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
                onChange={(e) => handleInputChange(insuranceId, field.id, e.target.value)}
                value={formData[insuranceId]?.[field.id] || ''}
              />
            </div>
          );
        case 'number':
          return (
            <div key={field.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <input
                type="number"
                required={field.required}
                className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
                onChange={(e) => handleInputChange(insuranceId, field.id, parseInt(e.target.value))}
                value={formData[insuranceId]?.[field.id] || ''}
              />
            </div>
          );
        case 'select':
          return (
            <div key={field.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {field.label}
              </label>
              <select
                required={field.required}
                className="w-full rounded-lg border-gray-300 focus:border-green-500 focus:ring-green-500"
                onChange={(e) => handleInputChange(insuranceId, field.id, e.target.value)}
                value={formData[insuranceId]?.[field.id] || ''}
              >
                <option value="">Vælg</option>
                {field.options?.map((option: string) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          );
        case 'radio':
          return (
            <div key={field.id} className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {field.label}
              </label>
              <div className="space-y-2">
                {field.options?.map((option: string) => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name={`${insuranceId}-${field.id}`}
                      value={option}
                      required={field.required}
                      className="text-green-600 focus:ring-green-500"
                      onChange={(e) => handleInputChange(insuranceId, field.id, e.target.value)}
                      checked={formData[insuranceId]?.[field.id] === option}
                    />
                    <span className="ml-2 text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        case 'checkbox':
          return (
            <div key={field.id} className="mb-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  onChange={(e) => handleInputChange(insuranceId, field.id, e.target.checked)}
                  checked={formData[insuranceId]?.[field.id] || false}
                />
                <span className="ml-2 text-sm text-gray-700">{field.label}</span>
              </label>
            </div>
          );
        default:
          return null;
      }
    });
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4 fade-in">
      <button
        onClick={onBack}
        className="text-green-600 hover:text-green-700 font-medium flex items-center mb-8 slide-up"
      >
        ← Tilbage
      </button>

      <div className="space-y-8">
        {selectedInsurances.map((insuranceId, index) => {
          const insurance = insuranceTypes.find(i => i.id === insuranceId);
          if (!insurance) return null;

          return (
            <div 
              key={insuranceId} 
              className="bg-white rounded-xl shadow-lg p-8 slide-up"
              style={{ animationDelay: `${0.1 * (index + 1)}s` }}
            >
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <DynamicIcon name={insurance.icon} className="w-6 h-6 mr-2 text-green-600" />
                {insurance.name}
              </h2>
              {renderFormFields(insurance.fields, insuranceId)}
            </div>
          );
        })}

        <button
          onClick={onComplete}
          className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-green-700 transition-colors slide-up"
          style={{ animationDelay: '0.3s' }}
        >
          Fortsæt
        </button>
      </div>
    </div>
  );
}